import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Box, Tooltip, Button, IconButton, Tabs, Tab, Link as MuiLink } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import DownloadIcon from "@mui/icons-material/Download";
import FullscreenIcon from "@mui/icons-material/Fullscreen";
import FullscreenExitIcon from "@mui/icons-material/FullscreenExit";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import DownloadingIcon from "@mui/icons-material/Downloading";
import DownloadForOfflineIcon from "@mui/icons-material/DownloadForOffline";
import AlertManager from "../../shared/Alert/Alert";
import { exportRowsToXlsx } from "../../shared/utils/exportUtils";
import { getDCCostMainRows } from "../../API/dcCostMainApi";
import { getDCCostLinkedRows } from "../../API/dcCostLinkedApi";
import { getDCCostColorRows } from "../../API/dcCostColorApi";
import { downloadDCCostApprovedCostFile, downloadDCCostDraftCostFile, downloadDCCostRejectedFile, downloadDCCostUnifiedFile } from "../../API/dcCostReportDownloadApi";
import resultsClasses from "../LandedCostResults/LandedCostResults.module.css";
import tableClasses from "../LandedCostUpload/LandedCostUpload.module.css";
import { Link } from "react-router-dom";

const approvalGroups = [
  {
    id: "145-DC",
    batchId: "145-DC",
    batchType: "Priority",
    runPriority: "High",
    typesOfItems: "LC priority",
    detailRows: [
      {
        id: "145-DC-Priority-01",
        batchId: "145-DC-priority",
        batchType: "Priority",
        totalSkus: 90,
        oldMargin: 45000,
        newMargin: 46300,
        percentChangeMargin: 2.9,
        dollarChangeMargin: 1300,
        status: "Initial",
        onHoldItems: 1,
        flaggedItems: 2,
        runPriority: "High",
        lastDateToApprove: "2026-07-28",
        typesOfItems: "LC priority",
      },
      {
        id: "145-DC-Priority-02",
        batchId: "145-DC-02",
        batchType: "Priority",
        totalSkus: 80,
        oldMargin: 38000,
        newMargin: 39050,
        percentChangeMargin: 2.8,
        dollarChangeMargin: 1050,
        status: "Partially approved",
        onHoldItems: 1,
        flaggedItems: 3,
        runPriority: "High",
        lastDateToApprove: "2026-07-29",
        typesOfItems: "LC priority",
      },
      {
        id: "145-DC-Priority-03",
        batchId: "145-DC-03",
        batchType: "Priority",
        totalSkus: 70,
        oldMargin: 42000,
        newMargin: 43150,
        percentChangeMargin: 2.7,
        dollarChangeMargin: 1150,
        status: "Escalated",
        onHoldItems: 1,
        flaggedItems: 2,
        runPriority: "High",
        lastDateToApprove: "2026-07-30",
        typesOfItems: "LC priority",
      },
    ],
  },
  {
    id: "146-DC",
    batchId: "146-DC",
    batchType: "Regular",
    runPriority: "Medium",
    typesOfItems: "LC others",
    detailRows: [
      {
        id: "146-DC-Regular-01",
        batchId: "146-DC-01",
        batchType: "Regular",
        totalSkus: 150,
        oldMargin: 30000,
        newMargin: 30750,
        percentChangeMargin: 2.5,
        dollarChangeMargin: 750,
        status: "Initial",
        onHoldItems: 2,
        flaggedItems: 4,
        runPriority: "Medium",
        lastDateToApprove: "2026-08-01",
        typesOfItems: "LC others",
      },
      {
        id: "146-DC-Regular-02",
        batchId: "146-DC-02",
        batchType: "Regular",
        totalSkus: 140,
        oldMargin: 27500,
        newMargin: 28100,
        percentChangeMargin: 2.2,
        dollarChangeMargin: 600,
        status: "Partially approved",
        onHoldItems: 1,
        flaggedItems: 3,
        runPriority: "Medium",
        lastDateToApprove: "2026-08-02",
        typesOfItems: "LC others",
      },
      {
        id: "146-DC-Regular-03",
        batchId: "146-DC-03",
        batchType: "Regular",
        totalSkus: 120,
        oldMargin: 27000,
        newMargin: 27300,
        percentChangeMargin: 1.1,
        dollarChangeMargin: 300,
        status: "Escalated",
        onHoldItems: 2,
        flaggedItems: 5,
        runPriority: "Medium",
        lastDateToApprove: "2026-08-03",
        typesOfItems: "LC others",
      },
    ],
  },
  {
    id: "147-DC",
    batchId: "147-DC",
    batchType: "New Items",
    runPriority: "High",
    typesOfItems: "New items",
    detailRows: [
      {
        id: "147-DC-New-01",
        batchId: "147-DC-01",
        batchType: "New Items",
        totalSkus: 50,
        oldMargin: 11000,
        newMargin: 11400,
        percentChangeMargin: 3.6,
        dollarChangeMargin: 400,
        status: "Initial",
        onHoldItems: 1,
        flaggedItems: 2,
        runPriority: "High",
        lastDateToApprove: "2026-07-28",
        typesOfItems: "New items",
      },
      {
        id: "147-DC-New-02",
        batchId: "147-DC-02",
        batchType: "New Items",
        totalSkus: 45,
        oldMargin: 12000,
        newMargin: 12550,
        percentChangeMargin: 4.6,
        dollarChangeMargin: 550,
        status: "Initial",
        onHoldItems: 0,
        flaggedItems: 2,
        runPriority: "High",
        lastDateToApprove: "2026-07-29",
        typesOfItems: "New items",
      },
    ],
  },
];

const aggregateBatch = (group) => {
  const rows = group.detailRows;
  const oldMargin = rows.reduce((total, row) => total + Number(row.oldMargin || 0), 0);
  const newMargin = rows.reduce((total, row) => total + Number(row.newMargin || 0), 0);
  const priorityCount = rows.filter((row) => row.typesOfItems === "LC priority").length;
  const regularCount = rows.filter((row) => row.typesOfItems === "LC others").length;
  const newCount = rows.filter((row) => row.typesOfItems === "New items").length;
  const rowStatuses = rows.map((row) => row.status);
  const status = rowStatuses.some((rowStatus) => rowStatus === "Partially approved")
    ? "Partially approved"
    : rowStatuses.every((rowStatus) => rowStatus === "Initial")
      ? "Initial"
      : rowStatuses.every((rowStatus) => rowStatus === "Escalated")
        ? "Escalated"
        : "Aggregated";

  return {
    id: group.id,
    batchId: group.batchId,
    batchType: group.batchType,
    totalSkus: rows.reduce((total, row) => total + Number(row.totalSkus || 0), 0),
    oldMargin,
    newMargin,
    percentChangeMargin: oldMargin ? ((newMargin - oldMargin) / oldMargin) * 100 : 0,
    dollarChangeMargin: newMargin - oldMargin,
    status,
    onHoldItems: rows.reduce((total, row) => total + Number(row.onHoldItems || 0), 0),
    flaggedItems: rows.reduce((total, row) => total + Number(row.flaggedItems || 0), 0),
    priorityCount,
    regularCount,
    newCount,
    lastDateToApprove: rows.reduce((earliest, row) => {
      if (!earliest) {
        return row.lastDateToApprove;
      }

      return row.lastDateToApprove < earliest ? row.lastDateToApprove : earliest;
    }, null),
    typesOfItems: group.typesOfItems,
    detailRows: rows,
  };
};

void approvalGroups;
void aggregateBatch;
const getBatchCacheKey = (batchId, batchType) => `${batchId || ""}::${batchType || ""}`;
const getLinkedFileLabel = (row = {}, suffix) => `${row.batchId || row.id || "batch"}_${suffix}`;

export const getDaysLeftToApprove = (lastDateToApprove, referenceDate = new Date()) => {
  if (!lastDateToApprove) {
    return "";
  }

  const targetDate = new Date(lastDateToApprove);
  if (Number.isNaN(targetDate.getTime())) {
    return "";
  }

  const startOfReferenceDate = new Date(referenceDate);
  startOfReferenceDate.setHours(0, 0, 0, 0);
  targetDate.setHours(0, 0, 0, 0);

  const remainingDays = Math.ceil((targetDate.getTime() - startOfReferenceDate.getTime()) / (24 * 60 * 60 * 1000));
  return Math.max(0, remainingDays);
};

const getApprovalDaysLeftValue = (row, itemType) => {
  const lastDateToApprove = row?.lastDateToApprove || row?.lastApprovalDate || row?.lastDate;
  const hasExplicitDaysLeft =
    itemType === "priority"
      ? row?.prioritySlaDaysRemaining ?? row?.daysLeftToApprovePriority ?? row?.priorityDaysLeft
      : row?.regularSlaDaysRemaining ?? row?.daysLeftToApproveRegular ?? row?.regularDaysLeft;

  if (hasExplicitDaysLeft !== undefined && hasExplicitDaysLeft !== null && hasExplicitDaysLeft !== "") {
    return hasExplicitDaysLeft;
  }

  const relevantCount = itemType === "priority" ? row?.priorityCount : row?.regularCount;
  const relevantBatchType = itemType === "priority" ? "Priority" : "Regular";
  const relevantTypesOfItems = itemType === "priority" ? "LC priority" : "LC others";

  if (
    Number(relevantCount || 0) <= 0 &&
    row?.batchType !== relevantBatchType &&
    row?.typesOfItems !== relevantTypesOfItems
  ) {
    return "";
  }

  return getDaysLeftToApprove(lastDateToApprove);
};

const formatDateParts = (value) => {
  if (Array.isArray(value)) {
    const [year, month, day, hour, minute, second] = value;
    if (year && month && day) {
      const datePart = `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
      if (hour !== undefined) {
        return `${datePart} ${String(hour ?? 0).padStart(2, "0")}:${String(minute ?? 0).padStart(2, "0")}:${String(
          second ?? 0
        ).padStart(2, "0")}`;
      }
      return datePart;
    }
  }

  if (value instanceof Date) {
    return value.toISOString().slice(0, 19).replace("T", " ");
  }

  if (value === 0) {
    return "0";
  }

  return value ?? "";
};

const getLastUpdatedDateValue = (row) =>
  formatDateParts(
    row?.createDate ??
      row?.create_date ??
      row?.lastUpdatedDate ??
      row?.lastUpdated ??
      row?.updateDate ??
      row?.updatedDate ??
      row?.updatedAt ??
      row?.last_updated_date ??
      row?.updated_at ??
      row?.last_update_date ??
      ""
  );

const getLastDateToApproveValue = (row) =>
  formatDateParts(row?.lastDateToApprove ?? row?.lastApprovalDate ?? row?.lastDate ?? row?.approvalDate ?? "");

const createBatchLinkColumn = ({ field, headerName, suffix, toPath, flex = 0.9, minWidth = 140 }) => ({
  field,
  headerName,
  flex,
  minWidth,
  sortable: false,
  filterable: false,
  hideFromExport: true,
  renderCell: ({ row }) => {
    if (!row?.batchId) {
      return "-";
    }

    return (
      <MuiLink component={Link} to={`${toPath}?batchId=${encodeURIComponent(row.batchId)}`} underline="hover">
        {getLinkedFileLabel(row, suffix)}
      </MuiLink>
    );
  },
});

export const createApprovalColumns = ({ expandedBatchId, loadLinkedRows, onToggleDetail } = {}) => [
  {
    field: "detailToggle",
    headerName: "",
    width: 56,
    sortable: false,
    filterable: false,
    disableColumnMenu: true,
    hideFromExport: true,
    renderCell: ({ row }) => {
      if (!row.batchId) {
        return null;
      }

      const expanded = expandedBatchId === row.id;

      return (
        <IconButton
          size="small"
          aria-label={`${expanded ? "Collapse" : "Expand"} ${row.batchId} details`}
          aria-expanded={expanded}
          onClick={() => {
            if (typeof onToggleDetail === "function") {
              onToggleDetail(row, expanded);
            }
            if (!expanded && typeof loadLinkedRows === "function") {
              void loadLinkedRows(row.batchId, row.batchType);
            }
          }}
          sx={{ p: 0.25 }}
        >
          {expanded ? <KeyboardArrowUpIcon fontSize="small" /> : <KeyboardArrowDownIcon fontSize="small" />}
        </IconButton>
      );
    },
  },
  {
    field: "batchId",
    headerName: "Batch ID",
    flex: 1,
    minWidth: 140,
    renderCell: ({ value, row }) =>
      value ? (
        <Button
          size="small"
          variant="text"
          component={Link}
          to={`/dc-cost/approvals/detail?batchId=${encodeURIComponent(value)}&batchType=${encodeURIComponent(row.batchType || "")}`}
          sx={{ textTransform: "none", fontWeight: 600, padding: 0, minWidth: "auto" }}
        >
          {value}
        </Button>
      ) : (
        "-"
      ),
  },
  { field: "batchType", headerName: "Batch type", flex: 0.9, minWidth: 120 },
  {
    field: "totalSkus",
    headerName: "Total # of SKUs",
    flex: 0.9,
    minWidth: 130,
  },
  {
    field: "oldMarginDollar",
    headerName: "Old Margin $",
    flex: 0.9,
    minWidth: 120,
  },
  {
    field: "newMarginDollar",
    headerName: "New Margin $",
    flex: 0.9,
    minWidth: 120,
  },
  {
    field: "marginPercentChange",
    headerName: "% change in margin",
    flex: 1,
    minWidth: 140,
  },
  {
    field: "marginDollarChange",
    headerName: "$ change in margin",
    flex: 1,
    minWidth: 150,
  },
  {
    field: "status",
    headerName: "Status",
    flex: 0.9,
    minWidth: 140,
  },
  {
    field: "onHoldCount",
    headerName: "# On Hold Items",
    flex: 0.9,
    minWidth: 130,
  },
  {
    field: "flaggedCount",
    headerName: "# Flagged Items",
    flex: 0.9,
    minWidth: 130,
  },
  { field: "priorityCount", headerName: "# of priority", flex: 0.8, minWidth: 120 },
  { field: "regularCount", headerName: "# of regular item", flex: 0.9, minWidth: 140 },
  { field: "newCount", headerName: "# of new", flex: 0.8, minWidth: 110 },
  {
    field: "daysLeftToApprovePriority",
    headerName: "Days left to approve PRIORITY items",
    flex: 1.1,
    minWidth: 180,
    valueGetter: ({ row }) => getApprovalDaysLeftValue(row, "priority"),
  },
  {
    field: "daysLeftToApproveRegular",
    headerName: "Days left to approve REGULAR items",
    flex: 1.1,
    minWidth: 180,
    valueGetter: ({ row }) => getApprovalDaysLeftValue(row, "regular"),
  },
  createBatchLinkColumn({
    field: "linkToLc",
    headerName: "Link to LC",
    suffix: "lc",
    toPath: "/landed-cost/results",
    flex: 0.9,
    minWidth: 140,
  }),
  createBatchLinkColumn({
    field: "linkToCostFile",
    headerName: "Link to cost file",
    suffix: "cf",
    toPath: "/cost-upload-history",
    flex: 1,
    minWidth: 160,
  }),
  createBatchLinkColumn({
    field: "linkToCJW",
    headerName: "Link to CJW",
    suffix: "cjw",
    toPath: "/cjw-manage-history",
    flex: 0.9,
    minWidth: 140,
  }),
  {
    field: "lastUpdatedDate",
    headerName: "Last updated date",
    flex: 1,
    minWidth: 160,
    valueGetter: ({ row }) => getLastUpdatedDateValue(row),
  },
  {
    field: "downloadReports",
    headerName: "Download Reports",
    flex: 1.2,
    minWidth: 180,
    sortable: false,
    filterable: false,
    hideFromExport: true,
    renderCell: (params) => {
      const buttons = [
        { key: "DETAIL", tooltip: "Consolidated DC report", Icon: DownloadingIcon },
        { key: "EXCEPTIONAL", tooltip: "Exceptional report", Icon: DownloadForOfflineIcon },
        { key: "APPROVED", tooltip: "Consolidated approved cost file", Icon: DownloadIcon },
        { key: "DRAFT", tooltip: "Consolidated draft cost file", Icon: DownloadIcon },
      ];

      const handleDownload = async (reportKey) => {
        if (reportKey === "DETAIL") {
          await downloadDCCostUnifiedFile(params?.row?.batchId || params?.row?.id);
          return;
        }

        if (reportKey === "APPROVED") {
          await downloadDCCostApprovedCostFile(params?.row?.batchId || params?.row?.id);
          return;
        }

        if (reportKey === "EXCEPTIONAL") {
          await downloadDCCostRejectedFile(params?.row?.batchId || params?.row?.id);
          return;
        }

        if (reportKey === "DRAFT") {
          await downloadDCCostDraftCostFile(params?.row?.batchId || params?.row?.id);
          return;
        }

        AlertManager.info("Report download wiring pending.", { contactSupport: false });
      };

      return (
        <Box sx={{ display: "flex", gap: 0.75 }}>
          {buttons.map(({ key, tooltip, Icon }) => (
            <Tooltip key={`${params.id}-${key}`} title={tooltip}>
              <IconButton size="small" onClick={() => handleDownload(key)} sx={{ p: 0.25 }}>
                <Icon fontSize="small" sx={{ color: "#000" }} />
              </IconButton>
            </Tooltip>
          ))}
        </Box>
      );
    },
  },
];

export const createColorPlanColumns = (detailColumns) =>
  detailColumns
    .map((column) =>
      column.field === "status"
        ? {
            ...column,
            headerName: "Color",
            valueGetter: ({ row, value }) => row.color ?? row.dcEstimationStatus ?? value ?? row.status ?? "",
          }
        : column
    )
    .filter((column) => column.field !== "lastDateToApprove" && column.field !== "lastApprovalDate");

const DCCostApprovals = () => {
  const [approvalRows, setApprovalRows] = useState([]);
  const [approvalPageSize, setApprovalPageSize] = useState(10);
  const [expandedBatchId, setExpandedBatchId] = useState(null);
  const [linkedRowsByBatchId, setLinkedRowsByBatchId] = useState({});
  const [linkedRowsLoadingByBatchId, setLinkedRowsLoadingByBatchId] = useState({});
  const [linkedRowsErrorByBatchId, setLinkedRowsErrorByBatchId] = useState({});
  const [colorRowsByBatchId, setColorRowsByBatchId] = useState({});
  const [colorRowsLoadingByBatchId, setColorRowsLoadingByBatchId] = useState({});
  const [colorRowsErrorByBatchId, setColorRowsErrorByBatchId] = useState({});
  const [detailTableExpanded, setDetailTableExpanded] = useState(false);
  const [detailTab, setDetailTab] = useState("byPlans");
  const [detailPageSize, setDetailPageSize] = useState(5);
  const [isLoading, setIsLoading] = useState(true);

  const loadApprovalRows = useCallback(async () => {
    setIsLoading(true);

    try {
      const rows = await getDCCostMainRows();
      setApprovalRows(Array.isArray(rows) ? rows : []);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadApprovalRows();
  }, [loadApprovalRows]);

  const loadLinkedRows = useCallback(
    async (batchId, batchType) => {
      if (!batchId) {
        return [];
      }

      const batchCacheKey = getBatchCacheKey(batchId, batchType);

      if (linkedRowsByBatchId[batchCacheKey]) {
        return linkedRowsByBatchId[batchCacheKey];
      }

      setLinkedRowsLoadingByBatchId((currentLoadingState) => ({
        ...currentLoadingState,
        [batchCacheKey]: true,
      }));
      setLinkedRowsErrorByBatchId((currentErrorState) => ({
        ...currentErrorState,
        [batchCacheKey]: null,
      }));

      try {
        const rows = await getDCCostLinkedRows(batchId, batchType);
        const normalizedRows = Array.isArray(rows) ? rows : [];

        setLinkedRowsByBatchId((currentRowsByBatchId) => ({
          ...currentRowsByBatchId,
          [batchCacheKey]: normalizedRows,
        }));

        return normalizedRows;
      } catch (error) {
        const errorMessage = error?.message || "Unable to load linked DC batches.";
        setLinkedRowsErrorByBatchId((currentErrorState) => ({
          ...currentErrorState,
          [batchCacheKey]: errorMessage,
        }));
        return [];
      } finally {
        setLinkedRowsLoadingByBatchId((currentLoadingState) => ({
          ...currentLoadingState,
          [batchCacheKey]: false,
        }));
      }
    },
    [linkedRowsByBatchId]
  );

  const loadColorRows = useCallback(
    async (batchId, batchType) => {
      if (!batchId) {
        return [];
      }

      const batchCacheKey = getBatchCacheKey(batchId, batchType);

      if (colorRowsByBatchId[batchCacheKey]) {
        return colorRowsByBatchId[batchCacheKey];
      }

      setColorRowsLoadingByBatchId((currentLoadingState) => ({
        ...currentLoadingState,
        [batchCacheKey]: true,
      }));
      setColorRowsErrorByBatchId((currentErrorState) => ({
        ...currentErrorState,
        [batchCacheKey]: null,
      }));

      try {
        const rows = await getDCCostColorRows(batchId, batchType);
        const normalizedRows = Array.isArray(rows) ? rows : [];

        setColorRowsByBatchId((currentRowsByBatchId) => ({
          ...currentRowsByBatchId,
          [batchCacheKey]: normalizedRows,
        }));

        return normalizedRows;
      } catch (error) {
        const errorMessage = error?.message || "Unable to load color plan DC batches.";
        setColorRowsErrorByBatchId((currentErrorState) => ({
          ...currentErrorState,
          [batchCacheKey]: errorMessage,
        }));
        return [];
      } finally {
        setColorRowsLoadingByBatchId((currentLoadingState) => ({
          ...currentLoadingState,
          [batchCacheKey]: false,
        }));
      }
    },
    [colorRowsByBatchId]
  );

  useEffect(() => {
    if (expandedBatchId && !approvalRows.some((row) => row.id === expandedBatchId)) {
      setExpandedBatchId(null);
      setDetailTableExpanded(false);
      setDetailTab("byPlans");
    }
  }, [approvalRows, expandedBatchId]);

  const expandedBatch = useMemo(
    () => approvalRows.find((row) => row.id === expandedBatchId) || null,
    [approvalRows, expandedBatchId]
  );

  const expandedBatchLinkedRows = useMemo(() => {
    if (!expandedBatch) {
      return [];
    }

    const cachedLinkedRows = linkedRowsByBatchId[getBatchCacheKey(expandedBatch.batchId, expandedBatch.batchType)];
    if (Array.isArray(cachedLinkedRows) && cachedLinkedRows.length) {
      return cachedLinkedRows;
    }

    return [];
  }, [expandedBatch, linkedRowsByBatchId]);

  const expandedBatchColorRows = useMemo(() => {
    if (!expandedBatch) {
      return [];
    }

    const cachedColorRows = colorRowsByBatchId[getBatchCacheKey(expandedBatch.batchId, expandedBatch.batchType)];
    if (Array.isArray(cachedColorRows) && cachedColorRows.length) {
      return cachedColorRows;
    }

    return [];
  }, [expandedBatch, colorRowsByBatchId]);

  const expandedBatchCacheKey = expandedBatch ? getBatchCacheKey(expandedBatch.batchId, expandedBatch.batchType) : null;
  const isExpandedBatchLoading = Boolean(
    expandedBatchCacheKey && linkedRowsLoadingByBatchId[expandedBatchCacheKey] && !expandedBatchLinkedRows.length
  );
  const expandedBatchError = expandedBatchCacheKey ? linkedRowsErrorByBatchId[expandedBatchCacheKey] : null;
  const isExpandedColorBatchLoading = Boolean(
    expandedBatchCacheKey && colorRowsLoadingByBatchId[expandedBatchCacheKey] && !expandedBatchColorRows.length
  );
  const expandedBatchColorError = expandedBatchCacheKey ? colorRowsErrorByBatchId[expandedBatchCacheKey] : null;

  useEffect(() => {
    if (!detailTableExpanded) {
      setDetailTab("byPlans");
    }
  }, [detailTableExpanded]);

  const handleDetailTabChange = useCallback(
    (_, newValue) => {
      setDetailTab(newValue);

      if (newValue === "byColorPlans" && expandedBatch) {
        void loadColorRows(expandedBatch.batchId, expandedBatch.batchType);
      }
    },
    [expandedBatch, loadColorRows]
  );

  const activeDetailRows = detailTab === "byColorPlans" ? expandedBatchColorRows : expandedBatchLinkedRows;
  const activeDetailLoading = detailTab === "byColorPlans" ? isExpandedColorBatchLoading : isExpandedBatchLoading;
  const activeDetailError = detailTab === "byColorPlans" ? expandedBatchColorError : expandedBatchError;

  const columns = useMemo(
    () =>
      createApprovalColumns({
        expandedBatchId,
        loadLinkedRows,
        onToggleDetail: (row, expanded) => {
          setExpandedBatchId(expanded ? null : row.id);
          setDetailTableExpanded(false);
          setDetailTab("byPlans");
        },
      }),
    [expandedBatchId, loadLinkedRows]
  );
  const detailColumns = useMemo(
    () => [
      {
        field: "batchId",
        headerName: "Batch ID",
        flex: 1,
        minWidth: 140,
        renderCell: ({ value }) => value || "-",
      },
      { field: "batchType", headerName: "Batch type", flex: 0.9, minWidth: 120 },
      {
        field: "oldMarginDollar",
        headerName: "Old Margin $",
        flex: 0.9,
        minWidth: 120,
      },
      {
        field: "newMarginDollar",
        headerName: "New Margin $",
        flex: 0.9,
        minWidth: 120,
      },
      {
        field: "marginPercentChange",
        headerName: "% change in margin",
        flex: 1,
        minWidth: 140,
      },
      {
        field: "marginDollarChange",
        headerName: "$ change in margin",
        flex: 1,
        minWidth: 150,
      },
      { field: "status", headerName: "Status", flex: 0.9, minWidth: 140 },
      {
        field: "onHoldCount",
        headerName: "# On Hold Items",
        flex: 0.9,
        minWidth: 130,
      },
      {
        field: "flaggedCount",
        headerName: "# Flagged Items",
        flex: 0.9,
        minWidth: 130,
      },
      {
        field: "lastDateToApprove",
        headerName: "Last date to approve",
        flex: 1,
        minWidth: 150,
        valueGetter: ({ row }) => getLastDateToApproveValue(row),
      },
    ],
    []
  );

  const handleExport = useCallback(() => {
    exportRowsToXlsx({
      columns,
      rows: approvalRows,
      fileName: "dc cost flow detail export.xlsx",
      sheetName: "DC Approvals",
      warningMessage: "No DC approvals data to export.",
    });
  }, [approvalRows, columns]);

  const renderPriorityDetailsTable = (isExpandedView, rows, columns = detailColumns) => (
    <div
      className={tableClasses.resultsTableCard}
      onClick={(event) => event.stopPropagation()}
      style={isExpandedView ? { flex: 1, minHeight: 0 } : undefined}
    >
      <DataGrid
        autoHeight={!isExpandedView}
        rows={rows}
        columns={columns}
        disableRowSelectionOnClick
        pageSize={detailPageSize}
        onPageSizeChange={(newPageSize) => {
          setDetailPageSize(newPageSize);
        }}
        rowsPerPageOptions={[5, 10, 25]}
        getRowId={(row) => row.id}
        hideFooter={!isExpandedView}
        hideFooterSelectedRowCount
        sx={isExpandedView ? { height: "100%" } : undefined}
      />
    </div>
  );

  const colorPlanColumns = useMemo(() => createColorPlanColumns(detailColumns), [detailColumns]);

  const renderColourTable = (isExpandedView) =>
    renderPriorityDetailsTable(isExpandedView, expandedBatchColorRows, colorPlanColumns);

  const renderPlansTable = (isExpandedView) => renderPriorityDetailsTable(isExpandedView, expandedBatchLinkedRows);

  const renderDetailTable = (isExpandedView = false) => {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 1,
          mt: isExpandedView ? 0 : 2,
          height: isExpandedView ? "100%" : "auto",
        }}
      >
        {expandedBatch ? (
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 2 }}>
            <Tabs
              value={detailTab}
              onChange={handleDetailTabChange}
              aria-label={`${expandedBatch.batchId} detail tabs`}
              variant="scrollable"
              scrollButtons="auto"
              sx={{ minHeight: 40, flex: 1 }}
            >
              <Tab
                value="byPlans"
                label={`${expandedBatch.batchId} by plans`}
                sx={{ textTransform: "none", minHeight: 40 }}
              />
              <Tab
                value="byColorPlans"
                label={`${expandedBatch.batchId} based on color plans`}
                sx={{ textTransform: "none", minHeight: 40 }}
              />
            </Tabs>
            <IconButton
              size="small"
              aria-label={isExpandedView ? "Collapse details table" : "Expand details table"}
              onClick={(event) => {
                event.stopPropagation();
                setDetailTableExpanded((currentValue) => !currentValue);
              }}
              sx={{ color: "#001489" }}
            >
              {isExpandedView ? <FullscreenExitIcon fontSize="small" /> : <FullscreenIcon fontSize="small" />}
            </IconButton>
          </Box>
        ) : null}
        {activeDetailLoading ? (
          <Box sx={{ color: "#4b5563", fontSize: 14 }}>Loading DC batches...</Box>
        ) : null}
        {activeDetailError ? (
          <Box sx={{ color: "#b91c1c", fontSize: 14 }}>{activeDetailError}</Box>
        ) : null}
        {expandedBatch && !activeDetailLoading && !activeDetailError && !activeDetailRows.length ? (
          <Box sx={{ color: "#4b5563", fontSize: 14 }}>
            No {detailTab === "byColorPlans" ? "color plan" : "linked"} rows were returned for this batch.
          </Box>
        ) : null}
        {detailTab === "byColorPlans" ? renderColourTable(isExpandedView) : renderPlansTable(isExpandedView)}
      </Box>
    );
  };

  return (
    <section className={resultsClasses.page}>
      <header className={resultsClasses.header}>
        <div>
          <h1 className={resultsClasses.title}>DC Cost Flow</h1>
          <p className={resultsClasses.subtitle}>
            Review DC cost batches awaiting approval and track escalation status.
          </p>
        </div>
      </header>

      <div className={resultsClasses.content}>
        <section className={resultsClasses.section}>
          <Box sx={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 1, mb: 0 }}>
            <Button
              variant="contained"
              size="small"
              startIcon={<DownloadIcon fontSize="small" />}
              onClick={handleExport}
              disabled={!approvalRows.length || isLoading}
              sx={{ textTransform: "none" }}
            >
              Export all
            </Button>
          </Box>
          {isLoading ? (
            <Box sx={{ color: "#4b5563", fontSize: 14, mb: 1 }}>
              Loading DC cost batches...
            </Box>
          ) : null}
          <div className={tableClasses.resultsTableCard}>
            <DataGrid
              autoHeight
              rows={approvalRows}
              columns={columns}
              disableRowSelectionOnClick
              pageSize={approvalPageSize}
              onPageSizeChange={(newPageSize) => {
                setApprovalPageSize(newPageSize);
              }}
              rowsPerPageOptions={[5, 10, 25]}
              pagination
              getRowId={(row) => row.id}
              hideFooterSelectedRowCount
            />
          </div>
          {expandedBatch ? (
            detailTableExpanded ? (
              <Box
                onClick={() => setDetailTableExpanded(false)}
                sx={{
                  position: "fixed",
                  inset: 0,
                  zIndex: (theme) => theme.zIndex.modal + 1,
                  bgcolor: "rgba(255, 255, 255, 0.96)",
                  p: 2,
                  display: "flex",
                  flexDirection: "column",
                  minHeight: 0,
                }}
              >
                <Box
                  onClick={(event) => event.stopPropagation()}
                  sx={{
                    width: "100%",
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    minHeight: 0,
                    gap: 1,
                  }}
                >
                  {renderDetailTable(true)}
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      px: 0.5,
                      pt: 0.5,
                      color: "text.secondary",
                      fontSize: 12,
                    }}
                  >
                    <span>Click outside this panel to return.</span>
                    <span>Expanded view footer</span>
                  </Box>
                </Box>
              </Box>
            ) : (
              renderDetailTable(false)
            )
          ) : null}
        </section>
      </div>
    </section>
  );
};

export default DCCostApprovals;


